import { useEffect } from 'react';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useRouter } from '@uirouter/react';

import { notifySuccess } from '@/portainer/services/notifications';

import { useCreateWorkflow } from '../queries/useCreateWorkflow';
import { FormValues } from '../wizard/types';
import { WizardStep, useWizardContext } from '../wizard/WizardContext';
import { WizardHeader } from '../wizard/WizardHeader';
import { WizardFooter } from '../wizard/WizardFooter';
import { createEmptyArtifact } from '../wizard/createEmptyArtifact';
import { getWorkflowLink } from '../utils';

import { toCreateWorkflowPayload } from './toCreateWorkflowPayload';

/** Matches the engine's own default when a workflow does not say. */
const defaultReconcileInterval = '5m';

type Props = {
  steps: WizardStep[];
};

function FormContent({ steps }: Props) {
  const { currentStep } = useWizardContext();
  const { validateForm } = useFormikContext<FormValues>();

  useEffect(() => {
    validateForm();
  }, [currentStep.id, validateForm]);

  return (
    <Form noValidate>
      <WizardHeader steps={steps} />
      <currentStep.Component />
      <WizardFooter />
    </Form>
  );
}

export function CreateForm({ steps }: Props) {
  const { currentStep, isLastStep, goToNextStep } = useWizardContext();
  const router = useRouter();
  const createWorkflowMutation = useCreateWorkflow();

  const initialFormValues: FormValues = {
    name: '',
    engine: 'edgeStack',
    sources: [],
    reconcile: { interval: defaultReconcileInterval },
    artifacts: [createEmptyArtifact()],
  };

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
      validationSchema={currentStep.validateStep}
    >
      <FormContent steps={steps} />
    </Formik>
  );

  function handleSubmit(
    formValues: FormValues,
    { setTouched, setSubmitting }: FormikHelpers<FormValues>
  ) {
    if (!isLastStep) {
      goToNextStep();
      setTouched({});
      setSubmitting(false);
      return;
    }

    createWorkflowMutation.mutate(toCreateWorkflowPayload(formValues), {
      onSuccess: (workflow) => {
        notifySuccess('Success', 'Workflow created');
        if (workflow.id) {
          const { to, params } = getWorkflowLink({ id: workflow.id });
          router.stateService.go(to, params);
        } else {
          router.stateService.go('^');
        }
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  }
}
